import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';

export const useHolidayCalendarApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    // api.js GET'te reddetmez: hata sinyali res.data.status.
    api.get(`/v1/holiday-calendars?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        setLoadFailed(res?.data?.status !== true);
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/holiday-calendars', formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/holiday-calendars/${id}`, formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  // Hazır (küresel) şablonu ya da şirket takvimini kiracının kendi takvimi olarak kopyalar (AKM POC §5.4).
  // Ad çakışması 409 holidayCalendar.nameExists (toast interceptor'da) → false, çekmece açık kalır.
  const copy = useCallback(async (id, name) => {
    try {
      await api.post(`/v1/holiday-calendars/${id}/copy`, { name });
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  // Türkiye resmî tatil takvimini kur ve ata (yalnız superAdmin). dryRun=true yalnız rapor üretir, hiçbir şey
  // yazmaz; dönüş TurkeyHolidaySeedResult (addedHolidayCount, assigned[], skippedAmbiguousCountry[] …) ya da null.
  const seedTurkey = useCallback(async (dryRun) => {
    try {
      const res = await api.post('/v1/holiday-calendars/turkey-defaults', null, { params: { dryRun } });
      if (!dryRun) setRefreshTrigger((p) => p + 1);
      return res?.data?.data || null;
    } catch {
      return null;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await api.delete(`/v1/holiday-calendars/${ids[0]}`);
      } else {
        await api.post('/v1/holiday-calendars/batch-delete', ids);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('holidayCalendar.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'countryCode', label: t('holidayCalendar.countryCode'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      // Kapsam: küresel (TenantId boş) = salt okunur hazır şablon; dolu = şirketin kendi takvimi.
      name: 'isGlobal', label: t('holidayCalendar.scope'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (v ? t('holidayCalendar.scopeGlobal') : t('holidayCalendar.scopeTenant')),
      },
    },
    { name: 'holidayCount', label: t('holidayCalendar.holidayCount'), options: { filter: false, sort: false } },
  ], [t]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('holidayCalendar.name'), type: 'text', grid: 12, required: true },
    {
      name: 'countryCode', label: t('holidayCalendar.countryCode'), type: 'select', grid: 12,
      options: (() => {
        try {
          const dn = new Intl.DisplayNames(['en'], { type: 'region' });
          const codes = ['TR','US','GB','DE','FR','RU','KZ','AZ','NL','BE','IT','ES','PT','GR','PL','CZ','AT','CH','SE','NO','DK','FI','IE','CA','AU','NZ','JP','KR','CN','IN','BR','MX','AR','SA','AE','QA','KW','EG','ZA','NG','KE','UA','GE','BY','BG','RO','HU','SK','HR','RS','BA','MK','AL','XK','ME','SI','LT','LV','EE','IS','MT','CY','LU','LI','MC','AD'];
          return codes.map(c => ({ id: c, name: `${c} — ${dn.of(c)}` })).sort((a, b) => a.name.localeCompare(b.name));
        } catch { return []; }
      })(),
      idKey: 'id', labelKey: 'name',
    },
  ], [t]);

  return { objectSchema, columns, data, fetching, loadFailed, pagination, create, update, _delete, copy, seedTurkey };
};
